import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginUser, updatePassword as apiUpdatePassword, updateUsername as apiUpdateUsername } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'admin' or 'customer'
  const [cart, setCart] = useState([]);

  useEffect(() => {
    // Check for stored auth
    const storedUser = localStorage.getItem('dresspay_user');
    const storedType = localStorage.getItem('dresspay_type');
    if (storedUser && storedType) {
      setUser(JSON.parse(storedUser));
      setUserType(storedType);
    }

    // Load cart from localStorage
    const storedCart = localStorage.getItem('dresspay_cart');
    if (storedCart) {
      setCart(JSON.parse(storedCart));
    }
  }, []);

  const login = async (username, password, type) => {
    try {
      const response = await loginUser(username, password, type);
      if (response.success && response.user) {
        setUser(response.user);
        setUserType(response.user_type);
        localStorage.setItem('dresspay_user', JSON.stringify(response.user));
        localStorage.setItem('dresspay_type', response.user_type);
        return { success: true };
      }
      return { success: false, message: response.message || 'Invalid credentials' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      await apiUpdatePassword(user.id, newPassword, userType);
      const updatedUser = { ...user, password: newPassword };
      setUser(updatedUser);
      localStorage.setItem('dresspay_user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      console.error('Password update error:', error);
      return { success: false };
    }
  };

  const updateUsername = async (newUsername) => {
    try {
      await apiUpdateUsername(user.id, newUsername);
      const updatedUser = { ...user, username: newUsername };
      setUser(updatedUser);
      localStorage.setItem('dresspay_user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      console.error('Username update error:', error);
      return { success: false };
    }
  };

  const logout = () => {
    setUser(null);
    setUserType(null);
    setCart([]);
    localStorage.removeItem('dresspay_user');
    localStorage.removeItem('dresspay_type');
    localStorage.removeItem('dresspay_cart');
  };

  const addToCart = (product) => {
    setCart(prevCart => {
      const existing = prevCart.find(item => item.id === product.id);
      let newCart;
      if (existing) {
        newCart = prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        newCart = [...prevCart, { ...product, quantity: 1 }];
      }
      localStorage.setItem('dresspay_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => {
      const newCart = prevCart.filter(item => item.id !== productId);
      localStorage.setItem('dresspay_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  const updateCartQuantity = (productId, quantity) => {
    setCart(prevCart => {
      const newCart = prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      );
      localStorage.setItem('dresspay_cart', JSON.stringify(newCart));
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('dresspay_cart');
  };

  const value = {
    user,
    userType,
    login,
    logout,
    updatePassword,
    updateUsername,
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};